import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Modal, Spinner } from 'react-bootstrap'
import { Dropdown } from 'primereact/dropdown'
import DropDownList from '../../listing/components/DropDownList'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import { PLATFORM_FIELD, PlatformDataType } from 'src/interfaces/Platform.interface'
import {
  createRecipeAsync,
  deleteRecipeMappingItemAsync,
  getCategoriesByPlatformIdAsync,
  getCategoryTemplateUsingTemplateIdAsync,
  getPlatformFieldOtherTemplatesAsync,
  getPlatformTitleDescriptionTemplateAsync,
  getRecipesAsyncUsingRecipeId,
  updateRecipeAsync,
} from 'src/redux/actions/templateAndPricingActions'
import { getPlatformPriceAsync } from 'src/redux/actions/productPricingAction'
import { getPlatformFieldsAsync } from 'src/redux/actions/productActions'
import { FieldArray, Formik } from 'formik'
import Select, { InputActionMeta, MultiValue } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import * as yup from 'yup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import useAppToast from 'src/hooks/useAppToast'
import {
  OTHER_TEMPLATE_DATA_ON_UPDATE,
  RECIPE,
  RECIPE_DATA,
  RECIPE_FORM_DATA,
  RECIPE_MAPPING,
} from 'src/interfaces/Template.interface'
import RecipeRuleItem from './RecipeRuleItem'
import AdditionalTemplateMappingItem from './AdditionalTemplateMappingItem'
import { useUserHavingWriteAccess } from 'src/utils/useUserHavingWriteAccess'
import { useCustomerAccess } from 'src/hooks/useCustomerAccess'
import { shouldShowAction, getActionButtonProps, getFormFieldDisabled } from 'src/utils/customerAccessUtils'
import { usePermission } from 'src/hooks/usePermission'
type AddNewRecipeModalPropsType = {
  isVisible: boolean
  closeModal: () => void
  selectedSecondaryPlatform: PlatformDataType | null
  getLatestRecipesList: () => void
  recipeData: RECIPE | null
}

const validateRequiredOtherTemplates = (
  fields: PLATFORM_FIELD[],
  otherTemplates: RECIPE_FORM_DATA['other_templates']
): string | null => {
  const missingField = fields.find((field) => {
    if (!field.isRequired) return false
    const selected = otherTemplates.find((item) => item?.fieldId === field.id)
    return !selected?.template_id
  })

  return missingField
    ? `Please select a value for ${missingField.display_name}`
    : null
}

const AddNewRecipeModal: FC<AddNewRecipeModalPropsType> = ({
  isVisible,
  closeModal,
  selectedSecondaryPlatform,
  getLatestRecipesList,
  recipeData = null,
}) => {
    const {isRead, isWrite, isDelete} = usePermission();
  const [templateDataLoading, setTemplateDataLoading] = useState(false)
  const [platformNativeFields, setPlatformNativeFields] = useState<PLATFORM_FIELD[] | []>([])
  const [platformCustomFields, setPlatformCustomFields] = useState<PLATFORM_FIELD[] | []>([])
  const { mainStoreData, erpStoreData, omsStoreData, accountingStoreData, shippingStoreData, fetchedSourcePlatformData } = useAppSelector((state) => state.fetchConnectedPlatform) || {}
  const apiSource = fetchedSourcePlatformData?.source_platform
  const sourceStore = apiSource?.id
    ? apiSource
    : mainStoreData ?? omsStoreData ?? erpStoreData ?? accountingStoreData ?? shippingStoreData ?? null
  const [isCustomField, setIsCustomField] = useState(false)
  const [customFieldLoading, setCustomFieldLoading] = useState(false)
  const [recipeDataDetails, setRecipeDataDetails] = useState<RECIPE_DATA | null | undefined>(null)
  const activeRecipeIdRef = useRef<number | null>(recipeData?.id ?? null)
  activeRecipeIdRef.current = recipeData?.id ?? null
  const dispatch = useAppDispatch()
  const { getTemplateData, platformCategories } = useAppSelector((state) => state.templateAndPricing)
  const [otherTemplateFieldLoader, setOtherTemplateFieldLoader] = useState(false)
  const [otherTemplateFieldData, setOtherTemplateFieldData] = useState<PLATFORM_FIELD[] | []>([])
  const [otherTemplateOptionsByFieldId, setOtherTemplateOptionsByFieldId] = useState<
    Record<number, { id: number; name: string }[]>
  >({})
  const [formReadyKey, setFormReadyKey] = useState(0)
  const { warningToast, successToast } = useAppToast()
  const [categoryOptions, setCategoryOptions] = useState<{label: string; value: string}[]>([])
  const [categorySearchLoading, setCategorySearchLoading] = useState(false)
  const [categoryPage, setCategoryPage] = useState(1)
  const [categoryHasMore, setCategoryHasMore] = useState(false)
  const [currentCategorySearch, setCurrentCategorySearch] = useState('')
  const [selectedCategoryOption, setSelectedCategoryOption] = useState<{label: string; value: string} | null>(null)
  const categorySearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [priceOptions, setPriceOptions] = useState<{label: string; value: string}[]>([])
  const [priceSearchLoading, setPriceSearchLoading] = useState(false)
  const [pricePage, setPricePage] = useState(1)
  const [priceHasMore, setPriceHasMore] = useState(false)
  const [currentPriceSearch, setCurrentPriceSearch] = useState('')
  const [selectedPriceOption, setSelectedPriceOption] = useState<{label: string; value: string} | null>(null)
  const priceSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const priceSelectionJustMade = useRef(false)
  const [enableButton, setEnableButton] = useState<boolean>(false);
  const [selectedRowsForDeletion, setSelectedRowsForDeletion] = useState<(number | null)[]>([]);
  const [isDisable, setIsDisable] = useState<boolean>(false);
  const { isUserHavingWriteAccess } = useUserHavingWriteAccess();
  const customerAccess = useCustomerAccess();
  const normalizeFieldsPayload = (payload: any): PLATFORM_FIELD[] => {
    if (Array.isArray(payload)) {
      return payload
    }
    if (Array.isArray(payload?.results)) {
      return payload.results
    }
    if (Array.isArray(payload?.data)) {
      return payload.data
    }
    if (Array.isArray(payload?.records)) {
      return payload.records
    }
    if (Array.isArray(payload?.items)) {
      return payload.items
    }
    return []
  }
  const fetchOtherTemplateOptions = async (
    fields: PLATFORM_FIELD[],
    platformId: number
  ): Promise<Record<number, { id: number; name: string }[]>> => {
    if (!fields.length) return {}

    const results = await Promise.all(
      fields.map(async (field) => {
        const response = await dispatch(
          getPlatformFieldOtherTemplatesAsync({
            platform_id: String(platformId),
            field_id: String(field.id),
          })
        )
        return {
          fieldId: field.id,
          options: (response?.payload?.results ?? []) as { id: number; name: string }[],
        }
      })
    )

    return results.reduce<Record<number, { id: number; name: string }[]>>((acc, { fieldId, options }) => {
      acc[fieldId] = options
      return acc
    }, {})
  }

  useEffect(() => {
    if (!isVisible || !selectedSecondaryPlatform?.id || !sourceStore?.id) {
      return
    }

    let cancelled = false
    const platformId = selectedSecondaryPlatform.id
    const sourcePlatformId = sourceStore.id

    const loadModalData = async () => {
      setTemplateDataLoading(true)
      setOtherTemplateFieldLoader(true)
      setIsDisable(true)
      setRecipeDataDetails(null)
      setSelectedPriceOption(null)
      setSelectedCategoryOption(null)
      setOtherTemplateFieldData([])
      setOtherTemplateOptionsByFieldId({})

      try {
        const [
          priceResponse,
          ,
          nativeFieldsResponse,
          customFieldsResponse,
          categoryResponse,
          otherFieldsResponse,
          recipeResponse,
        ] = await Promise.all([
          dispatch(
            getPlatformPriceAsync({
              platform_id: String(platformId),
              paginationdata: { page: 1, limit: 50, search: '' },
            })
          ),
          dispatch(
            getPlatformTitleDescriptionTemplateAsync({
              platform_id: String(platformId),
              paginationdata: { page: 1, limit: 100, search: '' },
            })
          ),
          dispatch(
            getPlatformFieldsAsync({
              platformId: sourcePlatformId,
              otherParams: {
                field_type: 'NATIVE',
                owner: 'PRODUCT',
                includeLocalFields: true,
                limit: 100,
                page: 1,
              },
            })
          ),
          dispatch(
            getPlatformFieldsAsync({
              platformId: sourcePlatformId,
              otherParams: {
                field_type: 'CUSTOM',
                owner: 'PRODUCT',
                includeLocalFields: false,
                limit: 100,
                page: 1,
              },
            })
          ),
          dispatch(
            getCategoriesByPlatformIdAsync({
              platformId: String(platformId),
              paginationdata: { page: 1, limit: 50, search: '' },
            })
          ),
          dispatch(
            getPlatformFieldsAsync({
              platformId: platformId,
              otherParams: { field_code: '', allowMapViaTemplate: true, owner: 'PRODUCT' },
            })
          ),
          recipeData?.id
            ? dispatch(
                getRecipesAsyncUsingRecipeId({
                  recipeId: String(recipeData.id),
                  otherParams: {},
                })
              )
            : Promise.resolve(null),
        ])

        if (cancelled) return

        const priceResults: { id: number | string; name: string }[] = priceResponse?.payload?.results ?? []
        const priceTotalCount: number = priceResponse?.payload?.count ?? 0
        const priceOpts = priceResults.map((item) => ({ value: String(item.id), label: item.name }))
        setPriceOptions(priceOpts)
        setPricePage(1)
        setCurrentPriceSearch('')
        setPriceHasMore(priceOpts.length < priceTotalCount)

        setPlatformNativeFields(normalizeFieldsPayload(nativeFieldsResponse?.payload))
        setPlatformCustomFields(normalizeFieldsPayload(customFieldsResponse?.payload))

        const categoryResults: { id: number | string; name: string }[] =
          categoryResponse?.payload?.results ?? []
        const categoryTotalCount: number = categoryResponse?.payload?.count ?? 0
        const categoryOpts = categoryResults.map((cat) => ({ value: String(cat.id), label: cat.name }))
        setCategoryOptions(categoryOpts)
        setCategoryPage(1)
        setCurrentCategorySearch('')
        setCategoryHasMore(categoryOpts.length < categoryTotalCount)

        const otherFields = normalizeFieldsPayload(otherFieldsResponse?.payload)
        setOtherTemplateFieldData(otherFields)
        if (!otherFields.length) {
          warningToast('Additional plans not found')
        }

        let loadedRecipeDetails: RECIPE_DATA | null = null
        if (
          recipeData?.id &&
          recipeResponse &&
          'payload' in recipeResponse &&
          recipeResponse.payload?.id === recipeData.id &&
          activeRecipeIdRef.current === recipeData.id
        ) {
          loadedRecipeDetails = recipeResponse.payload as RECIPE_DATA
          setRecipeDataDetails(loadedRecipeDetails)

          const priceTemplate = loadedRecipeDetails.price_template
          if (priceTemplate?.id && priceTemplate?.name) {
            setSelectedPriceOption({ value: String(priceTemplate.id), label: priceTemplate.name })
          }

          if (recipeData.category_template) {
            const categoryTemplateResponse = await dispatch(
              getCategoryTemplateUsingTemplateIdAsync({
                category_temp_id: Number(recipeData.category_template),
              })
            )
            if (cancelled) return

            const categoryTemplate = categoryTemplateResponse?.payload
            if (categoryTemplate?.id && categoryTemplate?.name) {
              const selectedCategory = {
                value: String(categoryTemplate.id),
                label: categoryTemplate.name,
              }
              setSelectedCategoryOption(selectedCategory)
              setCategoryOptions((prev) => {
                if (prev.some((opt) => opt.value === selectedCategory.value)) {
                  return prev
                }
                return [selectedCategory, ...prev]
              })
            }
          }
        }

        const templateOptions = await fetchOtherTemplateOptions(otherFields, platformId)
        if (cancelled) return

        setOtherTemplateOptionsByFieldId(templateOptions)
        setFormReadyKey((prev) => prev + 1)
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load listing plan modal data:', error)
        }
      } finally {
        if (!cancelled) {
          setTemplateDataLoading(false)
          setOtherTemplateFieldLoader(false)
          setIsDisable(false)
        }
      }
    }

    loadModalData()

    return () => {
      cancelled = true
    }
  }, [isVisible, selectedSecondaryPlatform?.id, sourceStore?.id, recipeData?.id, dispatch])

  const recipeSchema = yup.object().shape({
    name: yup.string().required('Name is required'),
    selectedPricingTemplate: yup.string().required('Price Modifier Rule is required'),
    selectedTitleDescriptionTemplate: yup
      .string()
      .required('Title Description Mapping Rule is required'),
    selectedCategoryTemplate: yup.string().required('Category Mapping Rule is required'),
    productRecipes: yup
      .array()
      .of(
        yup.object().shape({
          inputType: yup.string(),
          selectedAndOr: yup.string(),
          selectedFieldType: yup.string(),
          selectedCondition: yup.string(),
          selectedAnyAll: yup.string(),
        })
      )
      .required(),
  })

  useEffect(() => {
    if (!isVisible) {
      setSelectedCategoryOption(null)
      setCategoryOptions([])
      setCategoryPage(1)
      setCategoryHasMore(false)
      setCurrentCategorySearch('')
    }
  }, [isVisible])

  const initialValues: RECIPE_FORM_DATA = {
    name: recipeDataDetails?.name ?? recipeData?.name ?? '',
    selectedPricingTemplate: recipeDataDetails?.price_template?.id
      ? String(recipeDataDetails.price_template.id)
      : recipeData?.price_template
      ? String(recipeData.price_template)
      : '',
    selectedTitleDescriptionTemplate: recipeDataDetails?.title_description_template?.id
      ? String(recipeDataDetails.title_description_template.id)
      : recipeData?.title_description_template
      ? String(recipeData.title_description_template)
      : '',
    selectedCategoryTemplate: recipeDataDetails?.category_template?.id
      ? String(recipeDataDetails.category_template.id)
      : recipeData?.category_template
      ? String(recipeData.category_template)
      : '',
    dataConjunction:
      recipeDataDetails?.check_data_conjunction ??
      recipeData?.check_data_conjunction ??
      'OR',
    productRecipes: recipeDataDetails?.mapping_data?.length
      ? recipeDataDetails.mapping_data.map((res) => ({
          id: Number(res.id),
          checkPlatformField: String(res.check_platform_field?.id ?? res.check_platform_field),
          checkOperator: res.check_operator ? res.check_operator : 'CONTAINS',
          checkConjunction: res.check_conjunction,
          checkValue: res.check_value,
        }))
      : recipeData?.mapping_data?.length
      ? recipeData.mapping_data.map((res: RECIPE_MAPPING) => ({
          id: res.id,
          checkPlatformField: res.check_platform_field + '',
          checkOperator: res.check_operator ? res.check_operator : 'CONTAINS',
          checkConjunction: res.check_conjunction,
          checkValue: res.check_value,
        }))
      : [
          {
            id: null,
            checkConjunction: 'OR',
            checkOperator: 'CONTAINS',
            checkPlatformField: platformNativeFields[0]?.id ? platformNativeFields[0]?.id + '' : '',
            checkValue: '',
          },
        ],
    other_templates: recipeDataDetails?.other_templates?.length
      ? recipeDataDetails.other_templates.map((item: OTHER_TEMPLATE_DATA_ON_UPDATE) => ({
          id: item.id,
          fieldId: item.template.platform_field.id,
          template_id: item.template.id,
        }))
      : [],
  }

  useEffect(() => {
    setEnableButton(!otherTemplateFieldLoader && !templateDataLoading)
  }, [otherTemplateFieldLoader, templateDataLoading])
  const categorySelectionJustMade = useRef(false)

  const fetchCategoryPage = useCallback(
    async (page: number, search: string, append: boolean) => {
      setCategorySearchLoading(true)
      const response = await dispatch(
        getCategoriesByPlatformIdAsync({
          platformId: selectedSecondaryPlatform!.id! + '',
          paginationdata: {page, limit: 50, search},
        })
      )
      const payload = response?.payload
      const results: {id: number | string; name: string}[] = payload?.results ?? []
      const totalCount: number = payload?.count ?? 0
      const newOptions = results.map((cat) => ({value: String(cat.id), label: cat.name}))
      setCategoryOptions((prev) => (append ? [...prev, ...newOptions] : newOptions))
      setCategoryPage(page)
      setCategoryHasMore((append ? categoryOptions.length + newOptions.length : newOptions.length) < totalCount)
      setCategorySearchLoading(false)
    },
    [dispatch, selectedSecondaryPlatform, categoryOptions.length]
  )

  const handleCategorySearch = useCallback(
    (inputValue: string) => {
      // react-select fires onInputChange with "" after an option is selected — skip that refetch
      if (inputValue === '' && categorySelectionJustMade.current) {
        categorySelectionJustMade.current = false
        return
      }
      if (categorySearchTimer.current) clearTimeout(categorySearchTimer.current)
      setCurrentCategorySearch(inputValue)
      categorySearchTimer.current = setTimeout(() => {
        fetchCategoryPage(1, inputValue, false)
      }, 400)
    },
    [fetchCategoryPage]
  )

  const handleCategoryScrollToBottom = useCallback(() => {
    if (categoryHasMore && !categorySearchLoading) {
      fetchCategoryPage(categoryPage + 1, currentCategorySearch, true)
    }
  }, [categoryHasMore, categorySearchLoading, categoryPage, currentCategorySearch, fetchCategoryPage])

  const fetchPricePage = useCallback(
    async (page: number, search: string, append: boolean) => {
      if (!selectedSecondaryPlatform?.id) return

      setPriceSearchLoading(true)
      const response = await dispatch(
        getPlatformPriceAsync({
          platform_id: selectedSecondaryPlatform.id + '',
          paginationdata: {page, limit: 50, search},
        })
      )
      const payload = response?.payload
      const results: {id: number | string; name: string}[] = payload?.results ?? []
      const totalCount: number = payload?.count ?? 0
      const newOptions = results.map((item) => ({value: String(item.id), label: item.name}))
      setPriceOptions((prev) => (append ? [...prev, ...newOptions] : newOptions))
      setPricePage(page)
      setPriceHasMore(
        (append ? priceOptions.length + newOptions.length : newOptions.length) < totalCount
      )
      setPriceSearchLoading(false)
    },
    [dispatch, priceOptions.length, selectedSecondaryPlatform?.id]
  )

  const handlePriceSearch = useCallback(
    (inputValue: string, actionMeta?: InputActionMeta) => {
      // react-select also fires set-value / menu-close with "". Those must not
      // replace the searched options or the selected value disappears.
      if (actionMeta && actionMeta.action !== 'input-change') {
        return
      }
      if (inputValue === '' && priceSelectionJustMade.current) {
        priceSelectionJustMade.current = false
        return
      }
      if (priceSearchTimer.current) clearTimeout(priceSearchTimer.current)
      setCurrentPriceSearch(inputValue)
      priceSearchTimer.current = setTimeout(() => {
        fetchPricePage(1, inputValue, false)
      }, 400)
    },
    [fetchPricePage]
  )

  const handlePriceScrollToBottom = useCallback(() => {
    if (priceHasMore && !priceSearchLoading) {
      fetchPricePage(pricePage + 1, currentPriceSearch, true)
    }
  }, [priceHasMore, priceSearchLoading, pricePage, currentPriceSearch, fetchPricePage])

  // format for select drop down
  type CategoryOption = {
    label: string
    value: string
  }
  // Make sure this list is formatted correctly
  const selectOptionFormat = (options) => {
    const format: CategoryOption[] = options.map((cat) => ({
      value: cat.id,
      label: cat.name,
    }))
    return format
  }
  const { fetchedUserAccountData } = useAppSelector(state=>state.userAccountData)
  const getTemplateDatadata = selectOptionFormat(getTemplateData)
  const platformCategoriesData = selectOptionFormat(platformCategories)

  return (
    <>
      <Modal
        size={'xl'}
        backdrop='static'
        centered
        show={isVisible}
        onHide={() => {
          setOtherTemplateFieldData([])
          setOtherTemplateOptionsByFieldId({})
          setPriceOptions([])
          setSelectedPriceOption(null)
          setCurrentPriceSearch('')
          setPricePage(1)
          setPriceHasMore(false)
          closeModal()
        }}
      >
        {templateDataLoading ? (
          <div
            style={{ minHeight: '60vh' }}
            className='d-flex flex-column justify-content-center align-items-center gap-3'
          >
            <Spinner animation='border' />
            <span className='text-muted'>Loading listing plan...</span>
          </div>
        ) : (
          <Formik
            initialValues={initialValues}
            key={`listing-plan-${recipeData?.id ?? 'new'}-${formReadyKey}`}
            onSubmit={(values, helpers) => {
              helpers.setSubmitting(true)
              if (recipeData) {
                //that means call update
                if(values.name.trim() === "") {
                  warningToast(`Name can't be Empty`)
                  helpers.setSubmitting(false)
                  return;
                }
                
                if(Number(values.selectedTitleDescriptionTemplate) === 0) {
                  warningToast(`Please Select Value for Title Description Mapping Rule`)
                  helpers.setSubmitting(false)
                  return;
                }

                if(Number(values.selectedCategoryTemplate) === 0) {
                  warningToast(`Please Select Value for Category Mapping Rule`)
                  helpers.setSubmitting(false)
                  return;
                }

                const missingOtherTemplateMessage = validateRequiredOtherTemplates(
                  otherTemplateFieldData,
                  values.other_templates
                )
                if (missingOtherTemplateMessage) {
                  warningToast(missingOtherTemplateMessage)
                  helpers.setSubmitting(false)
                  return
                }

                const mappingData = values.productRecipes.filter(
                  (item) => !selectedRowsForDeletion.includes(item.id));
                const payload = {
                  platform: selectedSecondaryPlatform?.id,
                  name: values.name,
                    title_description_template: Number(values.selectedTitleDescriptionTemplate),
                    price_template: Number(values.selectedPricingTemplate) === 0 ? null : Number(values.selectedPricingTemplate),
                    category_template: Number(values.selectedCategoryTemplate),
                  check_data_conjunction: values.dataConjunction, //# AND, OR
                  mapping_data: mappingData.map((item) => {
                    const normalizedCheckValue =
                      typeof item.checkValue === 'string'
                        ? item.checkValue
                        : Array.isArray(item.checkValue)
                        ? (item.checkValue as string[]).join(',')
                        : item.checkValue === null || item.checkValue === undefined
                        ? ''
                        : String(item.checkValue)

                    if(normalizedCheckValue.trim() === "") {
                      warningToast(`Please Add Value for Condition Evaluator`)
                      helpers.setSubmitting(false)
                      return;
                    }
                    return {
                      id: item.id,
                      check_platform_field: item.checkPlatformField,
                      check_operator: item.checkOperator ? item.checkOperator : 'CONTAINS',
                      check_conjunction: item.checkConjunction,
                      check_value: normalizedCheckValue,
                    }
                  }),
                  other_templates: values.other_templates.filter((item) => {
                    if (item !== null) {
                      return item
                    }
                  }),
                }

                if (selectedRowsForDeletion.length > 0) {
                  const deletePromises = selectedRowsForDeletion
                    .filter(id => id !== null)
                    .map(id => {
                      // Dispatch each deletion thunk and return the promise it resolves
                      return dispatch(deleteRecipeMappingItemAsync({ templateMappingItemId: String(id) }));
                    });
                  Promise.all(deletePromises)
                    .then(() => {
                      setSelectedRowsForDeletion([]);
                      successToast("All selected items deleted successfully!");
                      dispatch(updateRecipeAsync({ recipeId: recipeData.id + '', payload: payload }))
                        .then((response) => {
                          if (response.payload.status == 400) {
                            warningToast(response.payload?.data?.error)
                            helpers.setSubmitting(false)
                          }
                          if (response.payload?.id) {
                            successToast('Listing plan updated successfully!')
                            helpers.setSubmitting(false)
                            getLatestRecipesList()
                            closeModal()
                          }
                        })
                        .catch((error) => {
                          console.log(' platform error', error)
                          helpers.setSubmitting(false)
                        }).finally(() => helpers.setSubmitting(false))
                    })
                    .catch(error => {
                      warningToast('Failed to delete some items.'); // Provide user feedback
                    })
                } else {
                  dispatch(updateRecipeAsync({ recipeId: recipeData.id + '', payload: payload }))
                    .then((response) => {
                      if (response.payload.status == 400) {
                        warningToast(response.payload?.data?.error)
                        helpers.setSubmitting(false)
                      }
                      if (response.payload?.id) {
                        successToast('Listing plan updated successfully!')
                        helpers.setSubmitting(false)
                        getLatestRecipesList()
                        closeModal()
                      }
                    })
                    .catch((error) => {
                      console.log(' platform error', error)
                      helpers.setSubmitting(false)
                    }).finally(() => helpers.setSubmitting(false))
                }
              } else {
                // call create
                if(values.name.trim() === "") {
                  warningToast(`Name can't be Empty`)
                  helpers.setSubmitting(false)
                  return;
                }
                
                if(Number(values.selectedTitleDescriptionTemplate) === 0) {
                  warningToast(`Please Select Value for Title Description Mapping Rule`)
                  helpers.setSubmitting(false)
                  return;
                }

                if(Number(values.selectedCategoryTemplate) === 0) {
                  warningToast(`Please Select Value for Category Mapping Rule`)
                  helpers.setSubmitting(false)
                  return;
                }

                const missingOtherTemplateMessage = validateRequiredOtherTemplates(
                  otherTemplateFieldData,
                  values.other_templates
                )
                if (missingOtherTemplateMessage) {
                  warningToast(missingOtherTemplateMessage)
                  helpers.setSubmitting(false)
                  return
                }

                const payload = {
                  platform: selectedSecondaryPlatform?.id,
                  name: values.name,
                   title_description_template: Number(values.selectedTitleDescriptionTemplate),
                    price_template: Number(values.selectedPricingTemplate) === 0 ? null : Number(values.selectedPricingTemplate),
                    category_template: Number(values.selectedCategoryTemplate),
                  check_data_conjunction: values.dataConjunction, //# AND, OR
                  mapping_data: values.productRecipes.map((item) => {
                    const normalizedCheckValue =
                      typeof item.checkValue === 'string'
                        ? item.checkValue
                        : Array.isArray(item.checkValue)
                        ? item.checkValue.join(',')
                        : item.checkValue === null || item.checkValue === undefined
                        ? ''
                        : String(item.checkValue)

                    if(normalizedCheckValue.trim() === "") {
                      warningToast(`Please Add Value for Condition Evaluator`)
                      helpers.setSubmitting(false)
                      return;
                    }
                    return {
                      check_platform_field: item.checkPlatformField,
                      check_operator: item.checkOperator ? item.checkOperator : 'CONTAINS',
                      check_conjunction: item.checkConjunction,
                      check_value: normalizedCheckValue,
                    }
                  }),
                  other_templates: values.other_templates.filter((item) => {
                    if (item !== null) {
                      return item
                    }
                  }),
                }

                dispatch(createRecipeAsync(payload))
                  .then((response) => {
                    if (response.payload.status == 400) {
                      warningToast(response.payload?.data?.error)
                      helpers.setSubmitting(false)
                    }
                    if (response.payload?.id) {
                      successToast('Listing plan saved successfully!')
                      helpers.setSubmitting(false)
                      getLatestRecipesList()
                      closeModal()
                    }

                    if (response.payload.status == 500) {
                      warningToast(response?.payload?.data)
                      helpers.setSubmitting(false)
                    }
                  })
                  .catch((error) => {
                    console.log(' platform error', error)
                    helpers.setSubmitting(false)
                  }).finally(()=>helpers.setSubmitting(false))
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFormikState,
              setSubmitting,
              resetForm,
              setFieldValue,
              /* and other goodies */
            }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <>
                    <Modal.Header className='border-0 pb-0 align-items-start'>
                      <div className='me-auto'>
                        <Modal.Title className='fw-semibold'>
                          {recipeData?.id ? 'Update Listing Plan' : 'Add New Listing Plan'}
                        </Modal.Title>
                        <small className='text-muted'>
                          Configure product criteria and listing rules for this plan.
                        </small>
                      </div>
                      <div className='d-flex align-items-center gap-2 ms-auto'>
                        <Button
                          disabled={isSubmitting}
                          size='sm'
                          variant='light'
                          onClick={() => {
                            closeModal()
                            setOtherTemplateFieldData([])
                            resetForm()
                          }}
                        >
                          Close
                        </Button>
                        {isWrite && shouldShowAction(customerAccess, 'edit') && (
                          <Button
                            {...getActionButtonProps(customerAccess, 'edit')}
                            disabled={
                              (recipeData?.id ? !enableButton : isSubmitting) ||
                              isDisable ||
                              getFormFieldDisabled(customerAccess)
                            }
                            size='sm'
                            variant='success'
                            type='submit'
                          >
                            {recipeData?.id
                              ? !enableButton
                                ? 'Saving'
                                : 'Save Changes'
                              : isSubmitting
                              ? 'Saving'
                              : 'Submit'}
                          </Button>
                        )}
                      </div>
                    </Modal.Header>

                    <Modal.Body className='px-4'>
                      <div className='d-flex flex-column gap-4'>
                        <section className='border rounded-3 p-4 bg-light'>
                          <div className='row g-3 align-items-start'>
                            <div className='col-12 col-lg-3'>
                              <label
                                className='form-label required-sm mb-1'
                                htmlFor='listing-plan-name'
                              >
                                Name
                              </label>
                              <span className='text-muted small d-block'>
                                Give the listing plan a descriptive name.
                              </span>
                            </div>
                            <div className='col-12 col-lg-9'>
                              <input
                                type='text'
                                id='listing-plan-name'
                                className='form-control'
                                autoComplete='off'
                                placeholder='Enter name'
                                name='name'
                                value={values.name}
                                onChange={handleChange}
                                required
                              />
                            </div>
                          </div>
                        </section>

                        <section className='border rounded-3 p-4'>
                          <div className='d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4'>
                            <div>
                              <h6 className='fw-semibold mb-1'>
                               <span className='required-sm'>Which Products you want to list? </span>
                              </h6>
                              <span className='text-muted small'>
                                Define how products qualify for this plan.
                              </span>
                            </div>
                             {isWrite &&( <button
                              type='button'
                              className='btn btn-primary btn-sm align-self-center'
                              onClick={() => {
                                setFormikState((pre) => ({
                                  ...pre,
                                  values: {
                                    ...pre.values,
                                    dataConjunction: values?.dataConjunction,
                                    productRecipes: [
                                      ...pre.values.productRecipes,
                                      {
                                        id: null,
                                        checkConjunction: 'OR',
                                        checkOperator: 'CONTAINS',
                                        checkPlatformField: platformNativeFields[0]?.id + '',
                                        checkValue: '',
                                      },
                                    ],
                                  },
                                }))
                              }}
                            >
                              + Add Condition
                            </button> )}
                          </div>

                          <div className='row gx-3 text-muted fw-semibold small mb-2 align-items-center'>
                            {values.productRecipes.length > 1 && (
                            <div className='col-12 col-lg-2 text-start mb-1 mb-lg-0'>
                              Select Condition
                            </div>
                            )}
                            <div className='col-12 col-lg-3 text-start mb-1 mb-lg-0'>
                              Select Field To Filter
                            </div>
                            <div className='col-12 col-lg-6 text-start'>Condition Evaluator</div>
                          </div>

                          <div className='d-flex flex-column gap-3'>
                            <>
                           {values.productRecipes.map((recipe, recipeIndex) => (
                              <RecipeRuleItem
                                key={'recipeitem' + recipeIndex}
                                customFieldLoading={false}
                                formValues={values}
                                getLatestRecipesList={getLatestRecipesList}
                                handleChange={handleChange}
                                recipe={recipe}
                                recipeData={recipeData}
                                recipeDataInDetail={
                                  recipeDataDetails?.mapping_data?.length
                                    ? recipeDataDetails.mapping_data[recipeIndex]
                                    : null
                                }
                                recipeIndex={recipeIndex}
                                setFormikState={setFormikState}
                                setSubmitting={setSubmitting}
                                platformCustomFields={platformCustomFields}
                                platformNativeFields={platformNativeFields}
                                selectedRowsForDeletion={selectedRowsForDeletion}
                                setSelectedRowsForDeletion={setSelectedRowsForDeletion}
                                hideConditionOnSingleRow={true}
                              />
                            ))}
                            </>
                          </div>
                        </section>

                        <section className='border rounded-3 p-4'>
                          <div className='mb-4'>
                            <h6 className='fw-semibold mb-1'>Listing Rules</h6>
                            <span className='text-muted small'>
                              Select the templates applied when listing products.
                            </span>
                          </div>

                          <div className='row g-4'>
                            <div className='col-12 col-lg-4'>
                              <label className='form-label mb-1'>Price Modifier Rule</label>
                              <Select<CategoryOption, false>
                                isSearchable
                                id='pricing-rule-dropdown'
                                options={priceOptions}
                                isLoading={priceSearchLoading}
                                filterOption={() => true}
                                onInputChange={(inputValue, actionMeta) =>
                                  handlePriceSearch(inputValue, actionMeta)
                                }
                                onMenuScrollToBottom={handlePriceScrollToBottom}
                                value={
                                  selectedPriceOption ||
                                  priceOptions.find(
                                    (opt) =>
                                      Number(opt.value) === Number(values.selectedPricingTemplate)
                                  ) ||
                                  null
                                }
                                onChange={(option) => {
                                  priceSelectionJustMade.current = true
                                  setSelectedPriceOption(option)
                                  setFieldValue('selectedPricingTemplate', option?.value || '')
                                }}
                                name='selectedPricingTemplate'
                              />
                            </div>
                            <div className='col-12 col-lg-4'>
                              <label className='form-label required-sm mb-1'>
                                Title Description Mapping Rule
                              </label>
                              <Select<CategoryOption, false>
                                id='title-description-dropdown'
                                isSearchable
                                required
                                name='selectedTitleDescriptionTemplate'
                                options={getTemplateDatadata}
                                value={
                                  getTemplateDatadata.find(
                                    (opt) =>
                                      Number(opt.value) ===
                                      Number(values.selectedTitleDescriptionTemplate)
                                  ) || null
                                }
                                onChange={(option) => {
                                  setFieldValue(
                                    'selectedTitleDescriptionTemplate',
                                    option?.value || ''
                                  )
                                }}
                              />
                            </div>
                            <div className='col-12 col-lg-4'>
                              <label className='form-label required-sm mb-1'>
                                Category Mapping Rule
                              </label>
                              <Select<CategoryOption, false>
                                isSearchable
                                required
                                options={categoryOptions}
                                id='category-template-dropdown'
                                name='selectedCategoryTemplate'
                                isLoading={categorySearchLoading}
                                filterOption={() => true}
                                onInputChange={(inputValue) => handleCategorySearch(inputValue)}
                                onMenuScrollToBottom={handleCategoryScrollToBottom}
                                value={selectedCategoryOption}
                                onChange={(option) => {
                                  categorySelectionJustMade.current = true
                                  setSelectedCategoryOption(option)
                                  setFieldValue('selectedCategoryTemplate', option?.value || '')
                                }}
                              />
                            </div>
                          </div>

                          <div className='mt-4'>
                            {otherTemplateFieldData.length > 0 ? (
                              <>
                                <div className='mb-3'>
                                  <h6 className='fw-semibold fs-6 mb-0'>Additional Template Rules</h6>
                                </div>
                                <FieldArray
                                  name='other_templates'
                                  render={() => (
                                    <div className='row g-4'>
                                      {otherTemplateFieldData.map((item: PLATFORM_FIELD, index) => (
                                        <AdditionalTemplateMappingItem
                                          key={'additionalmapping' + item.id}
                                          otherFieldIndex={index}
                                          item={item}
                                          values={values}
                                          setFormikState={setFormikState}
                                          selectedSecondaryPlatform={selectedSecondaryPlatform}
                                          handleChange={handleChange}
                                          prefetchedOptions={otherTemplateOptionsByFieldId[item.id] ?? []}
                                        />
                                      ))}
                                    </div>
                                  )}
                                />
                              </>
                            ) : null}
                          </div>
                        </section>
                      </div>
                    </Modal.Body>
                  </>
                </form>
              )
            }}
          </Formik>
        )}
      </Modal>
    </>
  )
}

export default AddNewRecipeModal
